import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AGENT_STATE_MAP,
  HOME_DASHBOARD,
  HOME_KPI_GROUPS,
  LEAD_STATUS_DEFS,
  REPORT_MODULE_DEFS,
  REPORT_MODULE_FEATURE,
  TENANT_STATUS_MAP,
  type AccessModule,
  type AgentState,
  type HomeKpi,
  type ReportModule,
} from '@goyone/shared';
import { api } from '../lib/api';
import { money, num, plural, relative, WORDS } from '../lib/format';
import { useAuth, useSession } from '../auth/AuthProvider';
import { Icon } from '../components/Icon';
import { Avatar, useTeamDirectory } from '../components/People';
import { Badge, PageHead, Stat } from '../components/ui';
import { formatValue } from '../components/Chart';
import { DashboardBoard, toBoardCards, usePublishedReports } from '../components/DashboardBoard';
import { ModuleDashboard } from './ModuleDashboard';

interface Workload {
  overdue: number;
  today: number;
  upcoming: number;
  unscheduled: number;
}

interface Stage {
  stage: string;
  label: string;
  color: string;
  count: number;
  value: number;
  weightedValue: number;
}

export function Home() {
  const session = useSession();
  const { hasFeature, can } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const status = TENANT_STATUS_MAP[session.tenant.status];
  const showReports = hasFeature('reports') && can('reports');

  /*
   * تبويباتُ اللوحات — «لوحتي» ثمّ لوحةُ كلّ وحدةٍ يملكها المستخدم.
   * التبديلُ في المكان لا بمغادرة الصفحة، فتُقرأ الشركةُ كلُّها من الرئيسية.
   */
  const moduleTabs = REPORT_MODULE_DEFS.filter(
    (m) => hasFeature(REPORT_MODULE_FEATURE[m.key]) && can(m.key),
  );
  const asked = params.get('tab');
  const active: 'home' | ReportModule =
    asked && moduleTabs.some((m) => m.key === asked) ? (asked as ReportModule) : 'home';

  return (
    <div className="page">
      <PageHead
        title={`أهلًا، ${session.user.name.split(' ')[0]}`}
        subtitle="ملخّص اليوم وما يحتاج متابعتك"
        actions={<Badge kind={status?.kind}>{status?.label}</Badge>}
      />

      {showReports && moduleTabs.length > 0 && (
        <div className="dash-tabs">
          <div className="tabs" role="tablist" aria-label="اللوحات">
            <button
              className="tab"
              role="tab"
              aria-selected={active === 'home'}
              onClick={() => setParams({}, { replace: true })}
            >
              لوحتي
            </button>
            {moduleTabs.map((m) => (
              <button
                key={m.key}
                className="tab"
                role="tab"
                aria-selected={active === m.key}
                onClick={() => setParams({ tab: m.key }, { replace: true })}
              >
                {m.label}
              </button>
            ))}
          </div>
          <button
            className="btn btn-primary btn-sm dash-new"
            onClick={() =>
              navigate(
                active === 'home' ? '/reports/builder?home=1' : `/reports/builder?module=${active}`,
              )
            }
            title="ابنِ تقريرًا وانشره على هذه اللوحة"
          >
            <Icon name="plus" size={14} />
            لوحة جديدة
          </button>
        </div>
      )}

      {active === 'home' ? (
        <HomeOverview showReports={showReports} />
      ) : (
        <ModuleDashboard module={active} embedded />
      )}
    </div>
  );
}

/* ==========================================================================
   لوحتي — لمحةُ اليوم والمؤشّرات
   ========================================================================== */

function HomeOverview({ showReports }: { showReports: boolean }) {
  const { atLeast, hasFeature, can } = useAuth();

  const workload = useQuery({
    queryKey: ['workload'],
    queryFn: () => api<Workload>('/crm/activities/my-workload'),
  });

  const leadStats = useQuery({
    queryKey: ['leads', 'stats'],
    queryFn: () => api<Record<string, number>>('/crm/leads/stats'),
  });

  /*
   * القمع من مساره الصحيح، ومجاميعُه من الخادم لا مجموعةً هنا.
   *
   * كان يُنادى `/crm/deals/pipeline` وقد انتقلت الصفقات إلى المبيعات، فيردّ
   * الخادم أربعمئة وأربعة وتظهر أرقام الرئيسية أصفارًا بلا سببٍ ظاهر.
   * والمسار يعيد المجاميع محسوبةً أصلًا، فجمعُها في الواجهة تكرارٌ يفترق
   * عنها يومًا.
   */
  const pipeline = useQuery({
    queryKey: ['pipeline'],
    queryFn: () =>
      api<{ stages: Stage[]; open: { count: number; value: number; weightedValue: number } }>(
        '/sales/deals/pipeline',
      ),
  });

  const openLeads = LEAD_STATUS_DEFS.filter((s) => !s.terminal).reduce(
    (sum, s) => sum + (leadStats.data?.[s.key] ?? 0),
    0,
  );
  const pipelineValue = pipeline.data?.open.value ?? 0;
  const openDeals = pipeline.data?.open.count ?? 0;

  /* المراقبةُ أداةُ إشراف: لمن يدير، وفي باقةٍ فيها إدارة العملاء. */
  const showMonitor = atLeast('MANAGER') && hasFeature('crm') && can('monitor');

  return (
    <>
      {/*
        المتأخر أولًا.
        الشاشة الافتتاحية تجيب سؤالًا واحدًا: ما الذي فات موعده؟
      */}
      {(workload.data?.overdue ?? 0) > 0 && (
        <div className="alert alert-warning mb-4">
          <Icon name="alert" size={18} />
          <div>
            لديك{' '}
            <span className="strong">{plural(workload.data?.overdue ?? 0, WORDS.activity)}</span>{' '}
            فات موعدها. <Link to="/my-work">عرضها</Link>
          </div>
        </div>
      )}

      {/* المؤشّراتُ الكبيرة: رقمٌ ونسبةُ تغيّرٍ عن الشهر السابق، مجمّعةً بأقسامها. */}
      {showReports && <KpiStrip />}

      {/* ما يخصّني اليوم — أرقامٌ تشغيليّةٌ لا مقارنةَ فيها. */}
      <div className="stats">
        <Stat label="متابعات اليوم" value={num(workload.data?.today ?? 0)} />
        <Stat
          label="متأخرة"
          value={
            <span
              style={{ color: (workload.data?.overdue ?? 0) > 0 ? 'var(--danger)' : undefined }}
            >
              {num(workload.data?.overdue ?? 0)}
            </span>
          }
        />
        <Stat label="عملاء قيد العمل" value={num(openLeads)} />
        <Stat label="صفقات مفتوحة" value={num(openDeals)} />
      </div>

      <div className="two-col">
        <div className="card">
          <div className="card-head">
            <h2 className="card-title">قمع المبيعات</h2>
            <Link to="/sales/pipeline" className="small" style={{ marginInlineStart: 'auto' }}>
              التفاصيل
            </Link>
          </div>
          <div className="card-body">
            {openDeals === 0 ? (
              <p className="mute small">لا توجد صفقات مفتوحة بعد.</p>
            ) : (
              <div className="funnel">
                {(pipeline.data?.stages ?? []).map((s) => (
                  <div
                    className="funnel-stage"
                    key={s.stage}
                    style={{ ['--stage-color' as string]: s.color }}
                  >
                    <div className="funnel-name">{s.label}</div>
                    <div className="funnel-count">{num(s.count)}</div>
                    <div className="funnel-value">{money(s.value)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2 className="card-title">العملاء حسب الحالة</h2>
            <Link to="/crm/leads" className="small" style={{ marginInlineStart: 'auto' }}>
              الكل
            </Link>
          </div>
          <div className="card-body">
            <div className="col">
              {LEAD_STATUS_DEFS.map((s) => {
                const count = leadStats.data?.[s.key] ?? 0;
                return (
                  <Link
                    key={s.key}
                    to={`/crm/leads?status=${s.key}`}
                    className="row"
                    style={{ justifyContent: 'space-between', color: 'inherit' }}
                  >
                    <span className="row">
                      <span
                        className="dot"
                        style={{ background: s.color, width: 8, height: 8 }}
                        aria-hidden
                      />
                      {s.label}
                    </span>
                    <span className="num strong">{num(count)}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {showMonitor && (
        <div className="mt-4">
          <MonitorSnapshot />
        </div>
      )}

      {/*
        لوحةُ النظام تحت لوحة اليوم.
        كلُّ تقارير الشركة وإحصاءاتها في مكانٍ واحد: بطاقاتٌ جاهزة من كلّ
        وحدةٍ يملكها المستخدم، وما نشره الفريقُ على الرئيسية من الباني —
        تُخفى وتُكبَّر وتُرتَّب، وكلُّ بطاقةٍ تُفتح في الباني لتعديلها.
      */}
      {showReports && <HomeBoard />}
    </>
  );
}

/* ==========================================================================
   المؤشّراتُ الكبيرة
   ========================================================================== */

function KpiStrip() {
  const { hasFeature, can } = useAuth();
  const kpis = useQuery({
    queryKey: ['home', 'kpis'],
    queryFn: () => api<{ items: HomeKpi[] }>('/reports/home-kpis'),
    staleTime: 5 * 60_000,
  });

  const items = (kpis.data?.items ?? []).filter(
    (k) => hasFeature(REPORT_MODULE_FEATURE[k.module]) && can(k.module),
  );
  const groups = HOME_KPI_GROUPS.map((g) => ({
    ...g,
    items: items.filter((i) => i.group === g.key),
  })).filter((g) => g.items.length > 0);

  if (kpis.isPending) return <div className="skeleton mb-5" style={{ height: 132 }} />;
  if (groups.length === 0) return null;

  return (
    <div className="kpi-wrap">
      {groups.map((g) => (
        <div className="kpi-group" key={g.key}>
          <div className="kpi-group-label">{g.label}</div>
          <div className="kpi-grid">
            {g.items.map((k) => (
              <KpiCard key={k.key} kpi={k} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function KpiCard({ kpi }: { kpi: HomeKpi }) {
  const up = (kpi.delta ?? 0) >= 0;
  return (
    <div className="kpi-card">
      <div className="kpi-label">{kpi.label}</div>
      <div className="kpi-value num">{formatValue(kpi.value, kpi.format)}</div>
      {kpi.delta !== null ? (
        <div className={`kpi-delta ${up ? 'is-up' : 'is-down'}`}>
          <Icon name={up ? 'arrowUp' : 'arrowDown'} size={15} />
          <span className="num">{Math.abs(kpi.delta)}%</span>
          <span className="kpi-delta-note">عن الشهر السابق</span>
        </div>
      ) : (
        <div className="kpi-delta">
          <span className="tiny mute">لا مقارنة بالشهر السابق</span>
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
   لوحةُ النظام
   ========================================================================== */

/** وحدةُ كلّ مصدرٍ في الكتالوج — لتُخفى بطاقةُ وحدةٍ لا يملكها المستخدم. */
const SOURCE_MODULE: Record<string, AccessModule> = {
  LEADS: 'crm',
  ACCOUNTS: 'crm',
  ACTIVITIES: 'crm',
  DEALS: 'sales',
  QUOTATIONS: 'sales',
  INVOICES: 'finance',
  PAYMENTS: 'finance',
  EXPENSES: 'finance',
  BILLS: 'finance',
};

function HomeBoard() {
  const { can, hasFeature } = useAuth();
  const published = usePublishedReports({ home: true });

  const prebuilt = HOME_DASHBOARD.filter((c) => {
    const module = SOURCE_MODULE[c.definition.source];
    return !module || (hasFeature(module === 'crm' ? 'crm' : module) && can(module));
  });

  return (
    <section className="mt-5">
      <div className="row mb-3" style={{ alignItems: 'baseline', gap: 'var(--sp-3)' }}>
        <h2 className="section-title" style={{ margin: 0 }}>
          لوحة النظام
        </h2>
        <span className="small mute">تقاريرُ الشركة كلِّها — والتفاصيلُ في لوحة كلّ وحدة</span>
      </div>
      {published.isPending && <div className="skeleton" style={{ height: 200 }} />}
      {published.data && (
        <DashboardBoard
          storageKey="goyone.dash.home"
          cards={toBoardCards(prebuilt, published.data.items)}
          addTo="/reports/builder?home=1"
        />
      )}
    </section>
  );
}

/* ==========================================================================
   مركز المراقبة — لمحة
   ========================================================================== */

interface BoardAgent {
  userId: string;
  state: AgentState;
  online: boolean;
  currentLabel: string | null;
  lastSeenAt: string | null;
  recordingOn: boolean;
}

/** كم يُعرض من المتّصلين — والبقيّةُ في شاشة المراقبة. */
const SNAPSHOT_ROWS = 6;

function MonitorSnapshot() {
  const { byId } = useTeamDirectory();

  /*
   * اللوحةُ نفسها التي تقرؤها شاشة المراقبة — لا مسارٌ ثانٍ يُحصي بطريقته.
   * وتُسأل كلّ نصف دقيقة: لمحةٌ في الرئيسية لا بثٌّ حيّ.
   */
  const board = useQuery({
    queryKey: ['cc', 'monitor-board'],
    queryFn: () => api<{ items: BoardAgent[] }>('/callcenter/monitor/board'),
    refetchInterval: 30_000,
  });

  const agents = board.data?.items ?? [];
  const online = agents.filter((a) => a.online);
  const recording = agents.filter((a) => a.recordingOn).length;

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">مركز المراقبة</h2>
        <Link to="/monitor" className="small" style={{ marginInlineStart: 'auto' }}>
          افتح الشاشة
        </Link>
      </div>
      <div className="card-body">
        {board.isPending && <div className="skeleton" style={{ height: 120 }} />}
        {board.isError && <p className="mute small">تعذّر جلب لوحة المراقبة.</p>}

        {board.data && (
          <>
            <div className="row" style={{ gap: 'var(--sp-4)', marginBlockEnd: 'var(--sp-3)' }}>
              <span>
                <span className="num strong" style={{ color: 'var(--success)' }}>
                  {num(online.length)}
                </span>{' '}
                <span className="small mute">متّصلٌ الآن من {num(agents.length)}</span>
              </span>
              {recording > 0 && (
                <span className="small mute">
                  <span className="num strong">{num(recording)}</span> يُسجَّل نشاطهم
                </span>
              )}
            </div>

            {online.length === 0 ? (
              <p className="mute small">لا أحد متّصلٌ الآن.</p>
            ) : (
              <ul
                className="col"
                style={{ listStyle: 'none', margin: 0, padding: 0, gap: 'var(--sp-2)' }}
              >
                {online.slice(0, SNAPSHOT_ROWS).map((a) => {
                  const person = byId.get(a.userId);
                  const state = a.state !== 'OFFLINE' ? AGENT_STATE_MAP[a.state] : null;
                  return (
                    <li key={a.userId} className="row" style={{ gap: 'var(--sp-2)' }}>
                      <Avatar name={person?.name ?? 'زميل'} src={person?.avatarUrl} size={28} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span className="small strong">{person?.name ?? 'زميل'}</span>
                        <span className="tiny mute" style={{ display: 'block' }}>
                          {a.currentLabel ? `على «${a.currentLabel}»` : 'بلا شاشةٍ معروفة'}
                          {a.lastSeenAt ? ` · ${relative(a.lastSeenAt)}` : ''}
                        </span>
                      </span>
                      {state && <Badge kind={state.kind}>{state.label}</Badge>}
                    </li>
                  );
                })}
              </ul>
            )}

            {online.length > SNAPSHOT_ROWS && (
              <p className="tiny mute mt-3">
                و{num(online.length - SNAPSHOT_ROWS)} غيرُهم —{' '}
                <Link to="/monitor">كلُّهم في الشاشة</Link>
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
